<template>
  <div class="px-4 sm:px-0">
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-2xl font-bold tracking-tight text-gray-900">{{ $gettext("Documents") }}</h2>
        <p class="mt-1 text-sm text-gray-500">{{ $gettext("Les documents de conformité de votre organisation.") }}</p>
      </div>
      <div v-if="organizationDocumentStore.all.length > 0" class="flex items-center gap-3">
        <button
          type="button"
          class="inline-flex items-center gap-x-1.5 rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          @click="showAddModal = true"
        >
          <PlusIcon class="-ml-0.5 size-4" aria-hidden="true" />
          {{ $gettext("Ajouter un document") }}
        </button>
      </div>
    </div>

    <div class="mt-6 flow-root">
      <!-- Empty state -->
      <div v-if="organizationDocumentStore.all.length === 0" class="text-center p-20">
        <div class="flex justify-center items-center">
          <FolderIcon class="size-12 text-gray-300" aria-hidden="true" />
        </div>
        <h3 class="mt-2 text-sm font-semibold text-gray-900">{{ $gettext("Pas encore de documents") }}</h3>
        <p class="mt-1 text-sm text-gray-500">{{ $gettext("Ajoutez vos politiques, procédures et autres documents de conformité.") }}</p>
        <div class="mt-6 flex justify-center">
          <button
            type="button"
            class="inline-flex items-center gap-x-1.5 rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition-colors"
            @click="showAddModal = true"
          >
            <PlusIcon class="-ml-0.5 size-4" aria-hidden="true" />
            {{ $gettext("Ajouter un document") }}
          </button>
        </div>
      </div>

      <!-- List -->
      <div v-else>
        <!-- Search + count -->
        <div class="flex items-center justify-between mb-4">
          <div class="relative w-80">
            <MagnifyingGlassIcon class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 size-4" />
            <input v-model="organizationDocumentStore.searchQuery" type="text" :placeholder="$gettext('Chercher un document...')"
              class="block w-full rounded-lg border-0 py-2 pl-9 pr-3 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-500" />
          </div>
          <span class="text-sm text-gray-500">{{ organizationDocumentStore.sorted.length }} {{ $gettext("documents") }}</span>
        </div>

        <!-- Table card -->
        <div class="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-900/5">
          <table v-if="organizationDocumentStore.sorted.length > 0" class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="py-3 pl-6 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{{ $gettext("Nom") }}</th>
                <th scope="col" class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{{ $gettext("Type") }}</th>
                <th scope="col" class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{{ $gettext("Dernière mise à jour") }}</th>
                <th scope="col" class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{{ $gettext("Statut") }}</th>
                <th scope="col" class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{{ $gettext("Fréquence de révision") }}</th>
                <th scope="col" class="relative py-3 pl-3 pr-6"></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr
                v-for="document in organizationDocumentStore.paginated"
                :key="document.id"
                class="hover:bg-gray-50 transition-colors cursor-pointer"
                @click="openVersionsModal(document)"
              >
                <td class="whitespace-nowrap py-3.5 pl-6 pr-3 text-sm font-medium text-gray-900">
                  <a
                    v-if="document.attributes.file"
                    :href="document.attributes.file.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center gap-x-2 text-indigo-600 hover:text-indigo-900 hover:underline"
                    @click.stop
                  >
                    <DocumentTextIcon class="size-4 shrink-0" aria-hidden="true" />
                    {{ document.attributes.name }}
                  </a>
                  <span v-else class="inline-flex items-center gap-x-2">
                    <DocumentTextIcon class="size-4 shrink-0 text-gray-400" aria-hidden="true" />
                    {{ document.attributes.name }}
                  </span>
                </td>
                <td class="whitespace-nowrap px-3 py-3.5 text-sm text-gray-500">
                  {{ documentTypeLabelsMap[document.attributes.documentType] }}
                </td>
                <td class="whitespace-nowrap px-3 py-3.5 text-sm text-gray-500">
                  {{ document.attributes.lastVersionDate }}
                </td>
                <td class="whitespace-nowrap px-3 py-3.5 text-sm text-gray-500">
                  <OrganizationDocumentStatusBadge :status="document.attributes.status" />
                </td>
                <td class="whitespace-nowrap px-3 py-3.5 text-sm text-gray-500">
                  {{ revisionFrequencyLabel(document) }}
                </td>
                <td class="relative whitespace-nowrap py-3.5 pl-3 pr-6 text-right text-sm font-medium">
                  <button
                    type="button"
                    class="rounded-md p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 transition-colors"
                    :title="$gettext('Supprimer')"
                    @click.stop="askDelete(document)"
                  >
                    <TrashIcon class="size-4" aria-hidden="true" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="text-center py-20">
            <h3 class="text-sm font-semibold text-gray-900">{{ $gettext("Pas de résultats trouvés") }}</h3>
          </div>
        </div>
        <PaginationNavigation :store="organizationDocumentStore" />
      </div>
    </div>

    <AddOrganizationDocumentModal :open="showAddModal" @close="showAddModal = false" />
    <OrganizationDocumentVersionsModal :open="showVersionsModal" :document-id="selectedDocumentId" @close="showVersionsModal = false" />
    <ConfirmationDialog
      v-model="showDeleteDialog"
      :title="$gettext('Supprimer le document')"
      :message="$gettext('Le document et tout son historique de versions seront définitivement supprimés. Cette action est irréversible.')"
      :confirm-text="$gettext('Supprimer')"
      :cancel-text="$gettext('Annuler')"
      @confirm="confirmDelete"
    />
  </div>
</template>

<script setup>
import { ref } from "vue";
import { PlusIcon } from "@heroicons/vue/20/solid";
import { MagnifyingGlassIcon, TrashIcon, DocumentTextIcon, FolderIcon } from "@heroicons/vue/24/outline";
import { useGettext } from "vue3-gettext";
import { useOrganizationDocumentStore } from "@/stores/organizationDocument.js";
import PaginationNavigation from "@/components/layout/PaginationNavigation.vue";
import ConfirmationDialog from "@/components/ui/ConfirmationDialog.vue";
import OrganizationDocumentStatusBadge from "@/components/organization-documents/OrganizationDocumentStatusBadge.vue";
import AddOrganizationDocumentModal from "@/components/organization-documents/AddOrganizationDocumentModal.vue";
import OrganizationDocumentVersionsModal from "@/components/organization-documents/OrganizationDocumentVersionsModal.vue";
import { documentTypeLabels, revisionFrequencyLabels } from "@/components/organization-documents/organizationDocumentLabels";

const { $gettext } = useGettext();
const organizationDocumentStore = useOrganizationDocumentStore();

const showAddModal = ref(false);
const showVersionsModal = ref(false);
const showDeleteDialog = ref(false);
const selectedDocumentId = ref(null);
const documentToDelete = ref(null);

const documentTypeLabelsMap = documentTypeLabels($gettext);
const revisionFrequencyLabelsMap = revisionFrequencyLabels($gettext);

const revisionFrequencyLabel = (document) => {
  const attributes = document.attributes;
  if (attributes.revisionFrequency === "other" && attributes.revisionFrequencyNote) {
    return attributes.revisionFrequencyNote;
  }
  return revisionFrequencyLabelsMap[attributes.revisionFrequency];
};

const openVersionsModal = (document) => {
  selectedDocumentId.value = document.id;
  showVersionsModal.value = true;
};

const askDelete = (document) => {
  documentToDelete.value = document;
  showDeleteDialog.value = true;
};

const confirmDelete = async () => {
  if (!documentToDelete.value) return;
  await organizationDocumentStore.destroy(documentToDelete.value.id);
  documentToDelete.value = null;
};
</script>
